import React, { ReactNode } from 'react';
import { ThisPaperDescription } from './this_paper_description';
import { BulkDownloadDescription } from './bulk_download_description';
import {
  BulkDownloadFilterCounts,
  getBulkDownloadFileList,
} from '../bulk_download_helper';
import $image_url from 'js/utils/image_url';
import classNames from 'classnames';
import ServerSideBulkDownloader from '../server_side_bulk_downloader';
import { recordBulkDownload } from '../record_bulk_download';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { WorkId } from 'js/liswp/types';
import * as Dismissibles from 'js/utils/dismissibles';
import { fsEvent } from 'js/fullstory';

const PREMIUM_ALL_PRODUCTS_DISMISSIBLE_KEY =
  'BulkDownloadPremiumModalAllProducts';

export type Props = {
  closeModal: () => void;
  work: {
    id: WorkId;
    title: string;
    slug: string;
  };
  filterCounts: BulkDownloadFilterCounts;
  downloadImmediately?: boolean; // defaults to false
};

type State = {
  downloading: boolean;
  paperCheck: boolean;
  relatedCheck: boolean;
};

type SelectableProductProps = {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
};

const SelectableProduct = (props: SelectableProductProps) => (
  <div
    onClick={props.onClick}
    className={classNames(
      'u-mv1x u-p5x u-displayFlex u-alignItemsCenter u-justifyContentSpaceEvenly u-clickable',
      {
        'all-products-modal-deselected-product': !props.selected,
        'all-products-modal-selected-product': props.selected,
      },
    )}
  >
    <div className="u-positionRelative u-clickable all-products-modal-premium-checkbox">
      <div className="all-products-modal-checkbox-cutout" />
      <div className="u-positionAbsolute all-products-modal-checkmark">
        <img
          src={$image_url('icons/thin-blue-check-mark.svg')}
          alt="check mark"
        />
      </div>
    </div>
    <div style={{ flexBasis: '80%' }}>{props.children}</div>
  </div>
);

export class PremiumReaderProductsModal extends React.Component<Props, State> {
  static defaultProps = {
    downloadImmediately: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      downloading: props.downloadImmediately ?? false,
      paperCheck: true,
      relatedCheck: true,
    };
  }

  componentDidMount() {
    Dismissibles.recordShow(PREMIUM_ALL_PRODUCTS_DISMISSIBLE_KEY);

    // Legacy FullStory API (kept for backwards compatibility)
    window.FS && window.FS.event('PremiumReaderProductsModal');

    fsEvent('BDPremium.Shown', {
      workId_int: Number(this.props.work.id),
      isPremium_bool: true,
      variant_str: 'modal',
      modalVariant_str: 'PremiumReaderProductsModal',
      downloadImmediately_bool: !!this.props.downloadImmediately,
    });
  }

  componentWillUnmount() {
    Dismissibles.recordDismissal(PREMIUM_ALL_PRODUCTS_DISMISSIBLE_KEY);
    fsEvent('BDPremium.Dismissed', {
      workId_int: Number(this.props.work.id),
      isPremium_bool: true,
      variant_str: 'modal',
      modalVariant_str: 'PremiumReaderProductsModal',
    });
  }

  render() {
    if (this.state.downloading) {
      return this.renderDownloader();
    } else {
      return this.renderProductSelector();
    }
  }

  renderProductSelector = () => {
    const { work, filterCounts } = this.props;
    const { paperCheck, relatedCheck } = this.state;
    return (
      <div className="DesignSystem all-products-modal-container premium-reader-modal">
        <div
          className="all-products-modal-close"
          onClick={this.props.closeModal}
        >
          <img
            src={$image_url('bulk_download/close-icon.png')}
            width="13"
            alt="close"
          />
        </div>
        <div className="u-mb9x u-fs30 u-lineHeight1_2 u-fw700">
          Select what to include in your package:
        </div>
        <SelectableProduct
          selected={paperCheck}
          onClick={() => this.invertCheck('paperCheck')}
        >
          <ThisPaperDescription workTitle={work.title} />
        </SelectableProduct>
        <SelectableProduct
          selected={relatedCheck}
          onClick={() => this.invertCheck('relatedCheck')}
        >
          <BulkDownloadDescription filterCounts={filterCounts} />
        </SelectableProduct>
        <div className="premium-reader-modal-sticky-button-container">
          <ImpressionWatcher
            source="premium_reader_products_modal_button"
            entity_type={2}
            entity_id={Number(work.id)}
            extra={this.trackingData}
          >
            <div className="u-displayFlex premium-reader-modal-button-container">
              <button
                className={classNames(
                  { 'u-bgColorCandy u-clickable': this.count !== 0 },
                  'u-pv4x u-ph7x u-displayFlex u-alignItemsCenter u-justifyContentCenter',
                )}
                style={{ borderRadius: 5, border: 0 }}
                onClick={this.startBulkDownload}
                disabled={this.count === 0}
              >
                <img
                  className="u-mr3x"
                  src={$image_url('icons/download-folder-down-arrow.svg')}
                  width="28"
                  alt="download icon"
                />
                <div
                  className="u-lineHeight1_2 u-tcWhite u-fw700"
                  style={{ fontSize: 17 }}
                >
                  Download Your PDF Package
                </div>
              </button>
            </div>
          </ImpressionWatcher>
        </div>
      </div>
    );
  };

  invertCheck = (check: keyof State) => {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(prevState: State) => { [x: stri... Remove this comment to see the full error message
    this.setState((prevState: State) => ({ [check]: !prevState[check] }));
  };

  startBulkDownload = () => {
    // By setting downloading: true, we switch to rendering a downloader
    // component (loading bar) that initiates the actual download.
    this.setState({ downloading: true });
  };

  renderDownloader = () => {
    const { work, closeModal } = this.props;

    return (
      <div className="DesignSystem">
        <div
          className="u-bgColorGrayLightest u-p6x"
          style={{ maxWidth: 800, borderRadius: 5 }}
        >
          <ServerSideBulkDownloader
            filesTotal={this.count}
            closeModal={closeModal}
            bundleDescription={work.slug}
            loadBulkDownloadFiles={this.getFileList}
            onZipUrlSuccess={(files) =>
              recordBulkDownload({ work_id: work.id.toString(), files })
            }
            source={'seed-work'}
          />
        </div>
      </div>
    );
  };

  getFileList = () => {
    const { work, filterCounts } = this.props;
    const { paperCheck, relatedCheck } = this.state;

    // This modal does not offer filter-level selection granularity.
    const selectedFilters = [];
    if (paperCheck) selectedFilters.push('thisPaper');
    if (relatedCheck) selectedFilters.push(...Object.keys(filterCounts));

    return getBulkDownloadFileList(
      work.id.toString(),
      selectedFilters,
      'server-side',
    );
  };

  get count() {
    const { filterCounts } = this.props;
    const { paperCheck, relatedCheck } = this.state;

    let count = 0;

    if (paperCheck) count++;
    if (relatedCheck) {
      count += Object.values(filterCounts).reduce(
        (a: number, b = 0) => a + b,
        0,
      );
    }

    return count;
  }

  get trackingData() {
    const { paperCheck, relatedCheck } = this.state;

    return {
      paper_selected: paperCheck,
      related_selected: relatedCheck,
    };
  }
}
